/**
 * お知らせAPI クライアント（本部 / R-Shift共通）
 */

import { authFetch, API_BASE_URL } from "./client";

/**
 * お知らせカテゴリレスポンスの型
 */
export interface NotificationCategoryResponse {
    id: number;
    code: string;
    name: string;
    color: string;
    display_order: number;
}

/**
 * お知らせレスポンスの型
 */
export interface NotificationResponse {
    id: number;
    sender_type: string;
    category: NotificationCategoryResponse;
    title: string;
    body: string;
    publish_start_date: string;
    publish_end_date: string | null;
    url: string | null;
    image_url: string | null;
    is_pinned: boolean;
    is_read: boolean;
    read_at: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * お知らせ一覧レスポンスの型
 */
export interface NotificationListResponse {
    items: NotificationResponse[];
    total: number;
}

/**
 * お知らせ作成リクエストの型
 */
export interface NotificationCreateRequest {
    sender_type?: string;
    category_id: number;
    title: string;
    body: string;
    publish_start_date: string;
    publish_end_date?: string | null;
    url?: string | null;
    image_url?: string | null;
}

/**
 * 既読登録レスポンスの型
 */
export interface MarkAsReadResponse {
    message: string;
    read_at: string | null;
}

/**
 * お知らせ一覧取得パラメータ
 */
interface FetchNotificationsParams {
    sender_type?: string;
    employee_id?: number;
    signal?: AbortSignal;
}

/**
 * お知らせカテゴリ一覧を取得
 */
export async function fetchNotificationCategories(
    signal?: AbortSignal,
): Promise<NotificationCategoryResponse[]> {
    const response = await authFetch(`${API_BASE_URL}/notification-categories`, { signal });

    if (!response.ok) {
        throw new Error(`Failed to fetch notification categories: ${response.status}`);
    }

    const data = await response.json();
    return data.items;
}

/**
 * お知らせ一覧を取得
 */
export async function fetchNotifications(
    params: FetchNotificationsParams = {},
): Promise<NotificationListResponse> {
    const { sender_type, employee_id, signal } = params;
    const searchParams = new URLSearchParams();
    if (sender_type) searchParams.set("sender_type", sender_type);
    if (employee_id != null) searchParams.set("employee_id", String(employee_id));

    const qs = searchParams.toString();
    const url = `${API_BASE_URL}/notifications${qs ? `?${qs}` : ""}`;

    const response = await authFetch(url, { signal });

    if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    return response.json();
}

/**
 * お知らせを作成
 */
export async function createNotification(
    data: NotificationCreateRequest,
): Promise<NotificationResponse> {
    const response = await authFetch(`${API_BASE_URL}/notifications`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        if (response.status === 400) {
            const error = await response.json();
            throw new Error(error.message || "入力内容に問題があります");
        }
        throw new Error(`Failed to create notification: ${response.status}`);
    }

    return response.json();
}

/**
 * お知らせを既読にする
 */
export async function markNotificationAsRead(
    notificationId: number,
    employeeId: number,
): Promise<MarkAsReadResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/notifications/${notificationId}/read`,
        {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ employee_id: employeeId }),
        },
    );

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("お知らせが見つかりません");
        }
        throw new Error(`Failed to mark notification as read: ${response.status}`);
    }

    return response.json();
}

/**
 * ピン留めトグルレスポンスの型
 */
export interface TogglePinResponse {
    message: string;
    is_pinned: boolean;
}

/**
 * お知らせのピン留めをトグル
 */
export async function toggleNotificationPin(
    notificationId: number,
): Promise<TogglePinResponse> {
    const response = await authFetch(
        `${API_BASE_URL}/notifications/${notificationId}/pin`,
        { method: "PATCH" },
    );

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("お知らせが見つかりません");
        }
        throw new Error(`Failed to toggle pin: ${response.status}`);
    }

    return response.json();
}

/**
 * 未読バッジ状態レスポンスの型
 */
export interface UnreadBadgesResponse {
    message: boolean;
    shift_request: boolean;
    shift_check: boolean;
    topicha: boolean;
}

/**
 * 未読バッジ状態を取得（軽量API）
 */
export async function fetchUnreadBadges(
    params: { tenantId: string; employeeId: number; storeId?: number; departmentId?: number },
    signal?: AbortSignal,
): Promise<UnreadBadgesResponse> {
    const searchParams = new URLSearchParams();
    searchParams.set("tenant_id", params.tenantId);
    searchParams.set("employee_id", String(params.employeeId));
    if (params.storeId != null) searchParams.set("store_id", String(params.storeId));
    if (params.departmentId != null) searchParams.set("department_id", String(params.departmentId));

    const response = await authFetch(
        `${API_BASE_URL}/notification/unread-badges?${searchParams.toString()}`,
        { signal },
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch unread badges: ${response.status}`);
    }

    return response.json();
}
